import React from "react";
import HighlightOffOutlinedIcon from "@mui/icons-material/HighlightOffOutlined";

export enum HPTagColor {
  GREEN = "green",
  BLUE = "blue",
  GRAY = "gray",
  RED = "red",
  YELLOW = "yellow",
}

interface TagProps {
  ariaLabel: string;
  children: React.ReactNode;
  color?: string | undefined;
  onClick?: (event: React.MouseEvent | React.KeyboardEvent) => void;
  classNames?: string;
  icon?: React.ReactNode;
  hasBorder?: boolean;
}

const tagColor = (color: string | undefined) => {
  switch (color) {
    case undefined:
      return "";
    case HPTagColor.GREEN:
      return "bg-[#E8F7F0] border-[#17B26A80]";
    case HPTagColor.BLUE:
      return "bg-[#F3F9FF] border-[#9BBCFD80]";
    case HPTagColor.GRAY:
      return "bg-[#F5F7FA] border-[#E0E6F0]";
    case HPTagColor.RED:
      return "bg-[#FDECEB] border-[#F0443880]";
    case HPTagColor.YELLOW:
      return "bg-[#FCF4E3] border-[#F5D9A3]";
    default:
      return "";
  }
};

const tagColorWithoutBorder = (color: string | undefined) => {
  switch (color) {
    case undefined:
      return "";
    case HPTagColor.GREEN:
      return "bg-[#E8F7F0]";
    case HPTagColor.BLUE:
      return "bg-[#F3F9FF]";
    case HPTagColor.GRAY:
      return "bg-[#F5F7FA]";
    case HPTagColor.RED:
      return "bg-[#FDECEB]";
    case HPTagColor.YELLOW:
      return "bg-[#FCF4E3]";
    default:
      return "";
  }
};

const iconColor = (color: string | undefined) => {
  switch (color) {
    case undefined:
      return "";
    case HPTagColor.GREEN:
      return "text-[#17B26A]";
    case HPTagColor.BLUE:
      return "text-[#2478FF]";
    case HPTagColor.GRAY:
      return "text-[#667085]";
    case HPTagColor.RED:
      return "text-[#F04438]";
    case HPTagColor.YELLOW:
      return "text-[#DC6803]";
    default:
      return "";
  }
};

const HPTag: React.FC<TagProps> = ({
  children,
  onClick,
  classNames = "",
  color = "blue",
  ariaLabel,
  icon,
  hasBorder = true,
}) => {
  const selectedTagColor = hasBorder
    ? tagColor(color)
    : tagColorWithoutBorder(color);
  const borderClass = hasBorder ? "border" : "";

  return (
    <div
      className={`px-2 py-1 w-fit h-fit rounded-sm capitalize flex text-xs ${selectedTagColor} ${borderClass} ${classNames} ${
        onClick ? "cursor-pointer hover:opacity-80" : ""
      }`}
      onClick={onClick as (event: React.MouseEvent) => void}
      aria-label={onClick ? ariaLabel : undefined}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick(e);
              }
            }
          : undefined
      }
    >
      <div className="flex items-center gap-1">
        {icon && <span className={iconColor(color)}>{icon}</span>}
        <span className="text-black">{children}</span>
      </div>
      {onClick && <HighlightOffOutlinedIcon className="ml-1 w-4 h-4 inline" />}
    </div>
  );
};

export default HPTag;
